import * as React from 'react'
import { cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type { VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'rounded-md border border-transparent bg-clip-padding text-sm font-medium inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 outline-none select-none focus-visible:ring-2 focus-visible:ring-offset-2',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary/80',
                outline:
                    'border-border bg-background hover:bg-muted hover:text-foreground shadow-xs',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-muted hover:text-foreground',
                destructive:
                    'bg-destructive/10 hover:bg-destructive/20 text-destructive focus-visible:border-destructive/40',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-11 gap-1.5 px-3 md:h-9',
                sm: 'h-11 gap-1 px-3 text-sm md:h-8 md:px-2.5 md:text-xs',
                lg: 'h-11 gap-1.5 px-4 md:h-10',
                icon: 'size-11 md:size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
)

function Button({
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean
    }) {
    const Comp = asChild ? Slot.Root : 'button'

    return (
        <Comp
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

export { Button, buttonVariants }
